import React, { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Avatar from '@mui/material/Avatar'
import { LockOutlined as LockOutlinedIcon } from '@mui/icons-material'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'

import ReCAPTCHA from 'react-google-recaptcha'

import useSubmitting from '../../hooks/useSubmitting'
import NoticeBox from './NoticeBox'
import { fieldErrorText, formatDeviseErrors, splitFieldErrors } from './deviseErrors'
import { deviseFetch, showFailedRequest } from './deviseRequest'
import { openidRetiredPost, openidSignInEnds } from './openidRetirement'
import { setFlashNotice, setFlashAlert, clearFlashAlert } from '../../redux/features/ui/uiSlice'
import { loadUserData, selectAuthToken, selectCurrentUser } from '../../redux/features/user/userSlice'

const CLAIM_FIELDS = ['login', 'email', 'password', 'password_confirmation']

// Shown instead of the normal authenticated app for a 0.6.27.13 "can still
// prove the old identity" account: signed in via OpenID, but with no
// `login` at all -- this account has never been through any sign-up
// machinery. Rather than a narrower "just confirm an email" step, this
// collects exactly what a new sign-up would (SignUp.jsx is the
// reference), pre-filled where possible from the provider's own
// nickname/email (login/email query params, see
// User::OmniauthCallbacksController#after_sign_in_path_for), and submits
// to the same #claim action that used to just take an email. Route is
// /app/migrate_openid_account (renamed 2026-10-14 from /app/claim_account,
// clearer about what it actually is); the backend endpoint it posts to
// (/user/claim.json) keeps its original name, an internal API detail
// rather than something a person sees.
export default function MigrateOpenidAccount() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const authToken = useSelector(selectAuthToken)
  const currentUser = useSelector(selectCurrentUser)
  const [recaptchaResponse, setRecaptchaResponse] = useState('')
  const [errors, setErrors] = useState({})
  const [submitting, submit] = useSubmitting()
  const recaptchaRef = useRef(null)

  // Pre-filled from whichever is available: this account's own fields,
  // if there's already a claim attempt to correct (a mistyped email,
  // most likely -- re-authenticating at the OpenID provider gets back
  // here with those fields freshly loaded, since #claim itself doesn't
  // stay signed in once it sets a login; see User#
  // active_for_authentication? and the omniauth callback), or otherwise
  // the provider's own nickname/email, query params set by
  // User::OmniauthCallbacksController#after_sign_in_path_for. Either way
  // this is only ever a suggestion, never a guarantee (someone else
  // could take a hint's value before this submits), which is why #claim
  // still validates for real regardless.
  const [searchParams] = useSearchParams()
  const loginHint = currentUser?.login || searchParams.get('login') || ''
  const emailHint = currentUser?.unconfirmed_email || searchParams.get('email') || ''
  const openidIdentifier = currentUser?.openid_identifier

  const rc_site_key = '6LfzDkccAAAAAO1K3DqAhD8Qar5jKxndtckhuaih'

  const handleSubmit = (evt) => {
    evt.preventDefault()
    setErrors({})
    dispatch(clearFlashAlert())

    const data = new FormData(evt.currentTarget)

    submit(async () => {
      const result = await deviseFetch(dispatch, '/user/claim.json', {
        method: 'POST',
        body: data,
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': authToken,
        },
      })

      if (result.failure) {
        showFailedRequest(dispatch, result)
        return
      }
      const { response, json: json_data } = result

      if (response.status === 201) {
        // Still signed in after this (see #claim) -- promote the fresh
        // login/unconfirmed_email into Redux before navigating, so route
        // guards and a possible later correction both see them right away
        // rather than waiting for the next full session fetch.
        dispatch(loadUserData(json_data))
        dispatch(setFlashNotice(
          'Account claimed successfully. Check your email to confirm it.'
        ))
        navigate('/app/confirmation_instructions')
        return
      }

      // A reCAPTCHA token is single-use server-side -- reset it on any
      // 422, the same as SignUp.jsx, so resubmitting after fixing an
      // unrelated field doesn't replay a now-consumed token.
      recaptchaRef.current?.reset()
      setRecaptchaResponse('')

      const { fieldErrors, flashMessage } = response.status === 422
        ? splitFieldErrors(json_data, CLAIM_FIELDS, 'this account from being claimed')
        : { fieldErrors: {}, flashMessage: formatDeviseErrors(json_data) }
      setErrors(fieldErrors)
      dispatch(setFlashAlert(flashMessage))
    })
  }

  const onRecaptchaChange = (value) => {
    setRecaptchaResponse(value)
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 9,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component='h1' variant='h5'>
          Migrate your account
        </Typography>

        <NoticeBox>
          OpenID sign-in ends on {openidSignInEnds}. Choose a login, a
          password and an email below to keep using Stikis.{' '}
          <Link component={RouterLink} to={openidRetiredPost} sx={{ color: 'primary.dark' }}>Read why</Link>
        </NoticeBox>

        {openidIdentifier && (
          // 0.6.27.13, 2026-10-16: shown so the person can see which
          // OpenID identity they're connecting their new login to.
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1, width: '100%', maxWidth: 400, wordBreak: 'break-all' }}
          >
            Migrating from OpenID identity: {openidIdentifier}
          </Typography>
        )}

        <Box
          component="form"
          sx={{ mt: 1, width: '100%', maxWidth: 400 }}
          onSubmit={handleSubmit}
          noValidate
        >
          <TextField
            margin='normal'
            required
            fullWidth
            label='Login'
            id='user[login]'
            name='user[login]'
            autoComplete='login'
            defaultValue={loginHint}
            error={Boolean(errors.login)}
            helperText={fieldErrorText(errors, 'login')}
            autoFocus
          />
          <TextField
            margin='normal'
            required
            fullWidth
            id='user[email]'
            label='Email address'
            name='user[email]'
            type='email'
            autoComplete='email'
            defaultValue={emailHint}
            error={Boolean(errors.email)}
            helperText={fieldErrorText(errors, 'email')}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="user[password]"
            label="Password"
            type="password"
            id="user[password]"
            autoComplete="new-password"
            error={Boolean(errors.password)}
            helperText={fieldErrorText(errors, 'password')}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="user[password_confirmation]"
            label="Confirm password"
            type="password"
            id="user[password_confirmation]"
            autoComplete="new-password"
            error={Boolean(errors.password_confirmation)}
            helperText={fieldErrorText(errors, 'password_confirmation')}
          />
          {/* The reCAPTCHA widget has a fixed ~304px width that doesn't shrink,
              so it can force horizontal overflow on narrow viewports. Scaling
              it down (with transform-origin left) keeps it within the form's
              width instead of widening the page -- same fix as SignUp.jsx. */}
          <Box
            sx={{
              width: '100%',
              overflow: 'hidden',
              transform: { xs: 'scale(0.85)', sm: 'none' },
              transformOrigin: 'left top',
            }}
          >
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={rc_site_key}
              onChange={onRecaptchaChange}
            />
          </Box>
          <input
            type="hidden"
            name="g-recaptcha-response"
            value={recaptchaResponse}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            loading={submitting}
            sx={{ mt: 3, mb: 2 }}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </Container>
  )
}
